import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useCreateNote } from "../../api/hooks/useCreateNote";
import { useDeleteNote } from "../../api/hooks/useDeleteNote";
import { useUpdateNote } from "../../api/hooks/useUpdateNote";
import type { components } from "../../api/schema";
import { TradeNoteEditor } from "../trades/TradeNoteEditor";
import { TradePicker } from "../trades/TradePicker";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

type NoteResponse = components["schemas"]["NoteResponse"];
type NoteType = components["schemas"]["NoteType"];
type TagResponse = components["schemas"]["TagResponse"];

// The row-click side panel that replaces the edit-note modal (ADR 0036 D8).
// Create mode and the trading_day / other edit modes use the explicit-save form;
// a trade note is edited through the shared autosave notepad (D3) — clearing it
// soft-deletes the note, so the trade branch needs no explicit delete control.
export type NotePanelState =
  | { mode: "create"; noteType?: NoteType; tradingDay?: string; tradeId?: string }
  | { mode: "edit"; note: NoteResponse };

interface NoteSidePanelProps {
  state: NotePanelState | null;
  onClose: () => void;
  tagOptions: ReadonlyArray<TagResponse>;
  tagName: (tagId: string) => string;
  // Resolved "SYMBOL · date · P&L" label for the edit-trade header; null falls
  // back to a generic title.
  tradeLabel?: string | null;
}

const PANEL_CONTENT_CLASS =
  "fixed inset-y-0 right-0 left-auto top-0 bottom-0 grid h-full max-h-screen w-full max-w-md translate-x-0 translate-y-0 grid-rows-[auto_1fr_auto] gap-0 overflow-hidden rounded-none border-l p-0 sm:rounded-none";

export function NoteSidePanel({
  state,
  onClose,
  tagOptions,
  tagName,
  tradeLabel,
}: NoteSidePanelProps) {
  const isTradeEdit = state?.mode === "edit" && state.note.note_type === "trade";
  return (
    <Dialog open={state !== null} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent aria-describedby={undefined} className={PANEL_CONTENT_CLASS}>
        {state === null ? null : isTradeEdit && state.mode === "edit" ? (
          <TradeNotePanelBody
            note={state.note}
            tradeLabel={tradeLabel ?? null}
            tagName={tagName}
            onClose={onClose}
          />
        ) : (
          <NoteFormPanelBody
            // Remount (re-seed) whenever the target note or create defaults change.
            key={state.mode === "edit" ? state.note.note_id : `create-${state.noteType ?? ""}-${state.tradingDay ?? ""}-${state.tradeId ?? ""}`}
            state={state}
            tagOptions={tagOptions}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface FormState {
  noteType: NoteType;
  body: string;
  tagIds: Set<string>;
  tradeId: string;
  tradingDay: string;
  favorited: boolean;
}

function seedFormState(state: NotePanelState): FormState {
  if (state.mode === "create") {
    return {
      noteType: state.noteType ?? "other",
      body: "",
      tagIds: new Set(),
      tradeId: state.tradeId ?? "",
      tradingDay: state.tradingDay ?? "",
      favorited: false,
    };
  }
  const { note } = state;
  return {
    noteType: note.note_type,
    body: note.body,
    tagIds: new Set(note.tag_ids),
    tradeId: note.trade_id ?? "",
    tradingDay: note.trading_day ?? "",
    favorited: note.favorited,
  };
}

// Create + (trading_day / other) edit: the explicit-save form, hosted in the
// side panel. Trade notes never reach here (handled by TradeNotePanelBody).
function NoteFormPanelBody({
  state,
  tagOptions,
  onClose,
}: {
  state: NotePanelState;
  tagOptions: ReadonlyArray<TagResponse>;
  onClose: () => void;
}) {
  const mode = state.mode;
  const editingNoteId = state.mode === "edit" ? state.note.note_id : null;
  const createMutation = useCreateNote();
  const updateMutation = useUpdateNote();
  const deleteMutation = useDeleteNote();
  const [form, setForm] = useState<FormState>(() => seedFormState(state));
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const trimmedTradeId = form.tradeId.trim();
    const trimmedTradingDay = form.tradingDay.trim();
    if (form.noteType === "trade" && trimmedTradeId === "") {
      setError("Trade is required when the note type is 'Trade'.");
      return;
    }
    if (form.noteType === "trading_day" && trimmedTradingDay === "") {
      setError("Trading day is required when the note type is 'Trading day'.");
      return;
    }
    const tagIds = Array.from(form.tagIds);
    try {
      if (mode === "create") {
        await createMutation.mutateAsync({
          note_type: form.noteType,
          body: form.body,
          favorited: form.favorited,
          tag_ids: tagIds,
          trade_id: form.noteType === "trade" ? trimmedTradeId : null,
          trading_day: form.noteType === "trading_day" ? trimmedTradingDay : null,
        });
      } else if (editingNoteId !== null) {
        await updateMutation.mutateAsync({
          noteId: editingNoteId,
          body: {
            note_type: form.noteType,
            body: form.body,
            tag_ids: tagIds,
            trade_id: form.noteType === "trade" ? trimmedTradeId : null,
            trading_day: form.noteType === "trading_day" ? trimmedTradingDay : null,
          },
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save the note.");
    }
  }

  async function handleDelete() {
    if (editingNoteId === null) return;
    setError(null);
    try {
      await deleteMutation.mutateAsync({ noteId: editingNoteId });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete the note.");
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <DialogHeader className="border-b border-outline-variant px-5 py-4">
        <DialogTitle>{mode === "create" ? "New note" : "Edit note"}</DialogTitle>
      </DialogHeader>

      <form
        id="note-side-panel-form"
        className="space-y-4 overflow-y-auto px-5 py-4"
        onSubmit={handleSubmit}
      >
        <div className="space-y-2">
          <Label htmlFor="note-type">Type</Label>
          <select
            id="note-type"
            data-testid="note-type-select"
            value={form.noteType}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                noteType: e.target.value as NoteType,
                tradeId: e.target.value === "trade" ? prev.tradeId : "",
                tradingDay: e.target.value === "trading_day" ? prev.tradingDay : "",
              }))
            }
            className="h-9 w-full rounded-control border border-outline bg-surface-container px-3 py-1 text-sm"
          >
            <option value="other">Other</option>
            <option value="trade">Trade</option>
            <option value="trading_day">Trading day</option>
          </select>
        </div>
        {form.noteType === "trade" ? (
          <div className="space-y-2">
            <Label htmlFor="note-trade">Trade</Label>
            <TradePicker
              id="note-trade"
              mode={mode}
              value={form.tradeId}
              onChange={(tradeId) => setForm((prev) => ({ ...prev, tradeId }))}
            />
          </div>
        ) : null}
        {form.noteType === "trading_day" ? (
          <div className="space-y-2">
            <Label htmlFor="note-trading-day">Trading day</Label>
            <Input
              id="note-trading-day"
              data-testid="note-trading-day-input"
              type="date"
              value={form.tradingDay}
              onChange={(e) => setForm((prev) => ({ ...prev, tradingDay: e.target.value }))}
            />
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="note-body">Body (markdown)</Label>
          <Textarea
            id="note-body"
            data-testid="note-body-input"
            rows={10}
            value={form.body}
            onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
            placeholder="Write your note…"
          />
        </div>
        <div className="space-y-2">
          <Label>Tags</Label>
          {tagOptions.length === 0 ? (
            <p className="text-xs text-on-surface-variant">
              No tags yet. <Link to="/tags" className="underline">Create a tag</Link>.
            </p>
          ) : (
            <ul
              data-testid="note-tag-list"
              className="flex max-h-32 flex-wrap gap-2 overflow-y-auto rounded-control border border-outline-variant p-2"
            >
              {tagOptions.map((tag) => {
                const checked = form.tagIds.has(tag.tag_id);
                return (
                  <li key={tag.tag_id}>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setForm((prev) => {
                            const next = new Set(prev.tagIds);
                            if (checked) next.delete(tag.tag_id);
                            else next.add(tag.tag_id);
                            return { ...prev, tagIds: next };
                          })
                        }
                      />
                      {tag.name}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {mode === "create" ? (
          <label className="flex items-center gap-2 text-sm text-on-surface">
            <input
              type="checkbox"
              checked={form.favorited}
              onChange={(e) => setForm((prev) => ({ ...prev, favorited: e.target.checked }))}
            />
            Mark as favorite
          </label>
        ) : null}
        {error !== null ? (
          <p role="alert" className="text-sm font-medium text-danger">
            {error}
          </p>
        ) : null}
      </form>

      <DialogFooter className="border-t border-outline-variant px-5 py-3">
        {mode === "edit" ? (
          <Button
            type="button"
            variant="outline"
            className="mr-auto text-danger hover:bg-danger-soft"
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
          >
            Delete note
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" form="note-side-panel-form" disabled={isPending}>
          {mode === "create" ? "Create note" : "Save changes"}
        </Button>
      </DialogFooter>
    </>
  );
}

// A trade note edited in the panel: the shared autosave notepad (ADR 0036 D3),
// the trade's existing tags shown read-only, and a link to the full trade. No
// Save / Delete buttons — autosave persists and an empty body soft-deletes.
function TradeNotePanelBody({
  note,
  tradeLabel,
  tagName,
  onClose,
}: {
  note: NoteResponse;
  tradeLabel: string | null;
  tagName: (tagId: string) => string;
  onClose: () => void;
}) {
  const tradeId = note.trade_id;
  return (
    <>
      <DialogHeader className="border-b border-outline-variant px-5 py-4">
        <DialogTitle className="font-display text-lg">
          {tradeLabel ?? "Trade note"}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-3 overflow-y-auto px-5 py-4">
        {tradeId !== null ? (
          <TradeNoteEditor tradeId={tradeId} />
        ) : (
          <p className="text-sm text-danger" role="alert">
            This trade note is missing its trade link.
          </p>
        )}
        {note.tag_ids.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {note.tag_ids.map((tagId) => (
              <li
                key={tagId}
                className="rounded-[9px] border border-outline-variant bg-surface-container-high px-1.5 py-0.5 text-[10px] font-semibold text-on-surface-variant before:text-outline before:content-['#']"
              >
                {tagName(tagId)}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="text-[11px] text-on-surface-muted">
          This note autosaves. Clearing all the text removes it.
        </p>
      </div>

      <DialogFooter className="border-t border-outline-variant px-5 py-3">
        {tradeId !== null ? (
          <Button asChild variant="outline">
            <Link
              to="/trades/$tradeId"
              params={{ tradeId }}
              search={{ from: "trades" }}
              onClick={onClose}
            >
              Go to trade
            </Link>
          </Button>
        ) : null}
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </DialogFooter>
    </>
  );
}
